<template>
  <div
    class="modal fade"
    id="lessonSelectionModal"
    tabindex="-1"
    aria-hidden="true"
    ref="modalRef"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
    >
      <div class="modal-content lesson-modal">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("qbank.new_test.select_lessons") }}</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <div v-if="categories.length === 0" class="empty-state">
            <i class="fas fa-folder-open"></i>
            <p>{{ $t("qbank.no_content") }}</p>
          </div>

          <div v-else class="categories-list">
            <div
              v-for="category in categories"
              :key="category.id"
              class="category-section"
              :class="{ disabled: isDisabled(category) }"
            >
              <!-- Category Header -->
              <div
                class="category-header"
                @click="!isDisabled(category) && toggleCategory(category)"
              >
                <div class="category-checkbox">
                  <input
                    type="checkbox"
                    :checked="isCategoryFullySelected(category)"
                    :indeterminate.prop="isCategoryPartiallySelected(category)"
                    :disabled="isDisabled(category)"
                    @click.stop="toggleCategory(category)"
                  />
                </div>
                <span class="category-name">{{ category.name }}</span>
                <span class="category-count"
                  >{{ getCategoryQuestionsCount(category) }}
                  {{ $t("qbank.questions") }}</span
                >
              </div>

              <!-- Lessons List -->
              <div class="lessons-list">
                <div
                  v-for="lesson in category.lessons"
                  :key="lesson.id"
                  class="lesson-item"
                  :class="{ disabled: isDisabled(category) }"
                  @click="
                    !isDisabled(category) && toggleLesson(lesson, category)
                  "
                >
                  <div class="lesson-checkbox">
                    <input
                      type="checkbox"
                      :checked="isLessonSelected(lesson)"
                      :disabled="isDisabled(category)"
                      @click.stop="toggleLesson(lesson, category)"
                    />
                  </div>
                  <span class="lesson-title">{{ lesson.title }}</span>
                  <span class="lesson-count">{{ lesson.questions_count }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-confirm"
            data-bs-dismiss="modal"
            @click="confirmSelection"
          >
            {{ $t("common.confirm") }} ({{ localSelectedLessons.length }})
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LessonSelectionModal",
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    selectedLessons: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:selected-lessons"],
  data() {
    return {
      localSelectedLessonIds: new Set(),
      selectedCategoryId: null,
      modalInstance: null,
    };
  },
  computed: {
    localSelectedLessons() {
      const allLessons = this.categories.flatMap((c) => c.lessons || []);
      return allLessons.filter((l) => this.localSelectedLessonIds.has(l.id));
    },
  },
  watch: {
    selectedLessons: {
      immediate: true,
      handler(newVal) {
        this.localSelectedLessonIds = new Set(newVal.map((l) => l.id));
        this.initSelectedCategory();
      },
    },
  },
  methods: {
    show() {
      this.localSelectedLessonIds = new Set(
        this.selectedLessons.map((l) => l.id)
      );
      this.initSelectedCategory();

      if (!this.modalInstance) {
        this.modalInstance = new bootstrap.Modal(this.$refs.modalRef);
      }
      this.modalInstance.show();
    },
    initSelectedCategory() {
      if (this.localSelectedLessonIds.size === 0) {
        this.selectedCategoryId = null;
        return;
      }

      for (const category of this.categories) {
        const categoryLessonIds = new Set(
          (category.lessons || []).map((l) => l.id)
        );
        const hasSelected = [...this.localSelectedLessonIds].some((id) =>
          categoryLessonIds.has(id)
        );
        if (hasSelected) {
          this.selectedCategoryId = category.id;
          break;
        }
      }
    },
    isDisabled(category) {
      return (
        this.selectedCategoryId !== null &&
        this.selectedCategoryId !== category.id
      );
    },
    isLessonSelected(lesson) {
      return this.localSelectedLessonIds.has(lesson.id);
    },
    isCategoryFullySelected(category) {
      const lessons = category.lessons || [];
      if (lessons.length === 0) return false;
      return lessons.every((l) => this.localSelectedLessonIds.has(l.id));
    },
    isCategoryPartiallySelected(category) {
      const lessons = category.lessons || [];
      const selectedCount = lessons.filter((l) =>
        this.localSelectedLessonIds.has(l.id)
      ).length;
      return selectedCount > 0 && selectedCount < lessons.length;
    },
    getCategoryQuestionsCount(category) {
      return (category.lessons || []).reduce(
        (sum, l) => sum + (l.questions_count || 0),
        0
      );
    },
    toggleLesson(lesson, category) {
      if (this.isDisabled(category)) return;

      if (this.localSelectedLessonIds.has(lesson.id)) {
        this.localSelectedLessonIds.delete(lesson.id);
        if (this.localSelectedLessonIds.size === 0) {
          this.selectedCategoryId = null;
        }
      } else {
        this.localSelectedLessonIds.add(lesson.id);
        this.selectedCategoryId = category.id;
      }
      // Force reactivity
      this.localSelectedLessonIds = new Set(this.localSelectedLessonIds);
    },
    toggleCategory(category) {
      if (this.isDisabled(category)) return;

      const lessons = category.lessons || [];
      const allSelected = this.isCategoryFullySelected(category);

      if (allSelected) {
        // Deselect all
        lessons.forEach((l) => this.localSelectedLessonIds.delete(l.id));
        if (this.localSelectedLessonIds.size === 0) {
          this.selectedCategoryId = null;
        }
      } else {
        // Select all
        lessons.forEach((l) => this.localSelectedLessonIds.add(l.id));
        this.selectedCategoryId = category.id;
      }
      // Force reactivity
      this.localSelectedLessonIds = new Set(this.localSelectedLessonIds);
    },
    confirmSelection() {
      this.$emit("update:selected-lessons", this.localSelectedLessons);
    },
  },
};
</script>

<style scoped>
.lesson-modal {
  border-radius: 20px;
  border: none;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 20px 24px;
}

.modal-title {
  font-weight: 700;
  font-size: 1.1rem;
}

.modal-header .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.modal-header .btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 0;
  max-height: 60vh;
  overflow-y: auto;
}

/* Scrollbar Styling - Green Theme */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(0, 196, 68, 0.08);
  border-radius: 4px;
  margin: 4px 0;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgb(0, 179, 62);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 179, 62, 0.8);
  background-clip: padding-box;
}

.modal-body::-webkit-scrollbar-thumb:active {
  background: rgba(0, 179, 62, 0.9);
  background-clip: padding-box;
}

/* Firefox scrollbar */
.modal-body {
  scrollbar-width: thin;
  scrollbar-color: rgb(0, 179, 62) rgba(0, 196, 68, 0.08);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
}

.categories-list {
  padding: 16px;
}

.category-section {
  margin-bottom: 16px;
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  transition: opacity 0.3s ease;
}

.category-section.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.category-header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: #f1f5f9;
  cursor: pointer;
  transition: background 0.3s ease;
}

.category-header:hover {
  background: #e2e8f0;
}

.category-checkbox {
  margin-right: 12px;
}

.category-checkbox input {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #00c744;
}

.category-name {
  flex: 1;
  font-weight: 700;
  color: #2d3748;
  font-size: 0.95rem;
}

.category-count {
  font-size: 0.8rem;
  color: #64748b;
  background: white;
  padding: 4px 10px;
  border-radius: 12px;
}

.lessons-list {
  padding: 8px 16px 16px;
}

.lesson-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-left: 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.lesson-item:hover {
  background: #e2e8f0;
}

.lesson-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.lesson-checkbox {
  margin-right: 12px;
}

.lesson-checkbox input {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #00c744;
}

.lesson-title {
  flex: 1;
  color: #475569;
  font-size: 0.9rem;
}

.lesson-count {
  font-size: 0.85rem;
  color: #0f172a;
  background: #cbd5e1;
  padding: 3px 10px;
  border-radius: 10px;
  font-weight: 800;
}

.modal-footer {
  border: none;
  padding: 16px 24px;
  background: #f8fafc;
}

.btn-confirm {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4);
  color: white;
}

/* RTL Support */
[dir="rtl"] .category-checkbox,
[dir="rtl"] .lesson-checkbox {
  margin-right: 0;
  margin-left: 12px;
}

[dir="rtl"] .lesson-item {
  margin-left: 0;
  margin-right: 20px;
}

/* Dark Mode */
[data-bs-theme="dark"] .lesson-modal,
.dark .lesson-modal {
  background: #1e293b;
}

[data-bs-theme="dark"] .category-section,
.dark .category-section {
  background: #334155;
}

[data-bs-theme="dark"] .category-header,
.dark .category-header {
  background: #475569;
}

[data-bs-theme="dark"] .category-header:hover,
.dark .category-header:hover {
  background: #526280;
}

[data-bs-theme="dark"] .category-name,
.dark .category-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .category-count,
.dark .category-count {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .lesson-item:hover,
.dark .lesson-item:hover {
  background: #475569;
}

[data-bs-theme="dark"] .lesson-title,
.dark .lesson-title {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .lesson-count,
.dark .lesson-count {
  background: #475569;
  color: #94a3b8;
}

[data-bs-theme="dark"] .modal-footer,
.dark .modal-footer {
  background: #1e293b;
}
</style>
